import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  AlertTriangle,
  ShieldCheck,
  Phone,
  Sparkles,
  RefreshCcw,
  Quote,
} from 'lucide-react';
import { allQuizzes, PROFESSIONAL_SIGN } from '../quizzes';
import { profile } from '../mock';

const Quiz = () => {
  const { quizId } = useParams();
  const quiz = allQuizzes[quizId];

  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    setStepIdx(0);
    setAnswers({});
    setShowResult(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [quizId]);

  // ============= Result computation (must run unconditionally) =============
  const result = useMemo(() => {
    if (!showResult || !quiz) return null;

    if (quiz.type === 'validated') {
      let total = 0;
      quiz.questions.forEach((q) => {
        const a = answers[q.id];
        if (a) total += a.score;
      });
      const band = quiz.resultBands.find((b) => total >= b.min && total <= b.max);
      const q10 = answers['q10'];
      const selfHarm = q10 && q10.score >= 1;
      return { type: 'validated', total, band, selfHarm };
    }

    if (quiz.type === 'reflective-category') {
      const counts = {};
      quiz.questions.forEach((q) => {
        const a = answers[q.id];
        if (a) counts[a.cat] = (counts[a.cat] || 0) + 1;
      });
      let topCat = null;
      let topCount = -1;
      Object.entries(counts).forEach(([cat, count]) => {
        if (count > topCount) {
          topCount = count;
          topCat = cat;
        }
      });
      const data = quiz.results[topCat];
      return { type: 'reflective-category', cat: topCat, data, counts };
    }

    if (quiz.type === 'reflective-score') {
      let total = 0;
      quiz.questions.forEach((q) => {
        const a = answers[q.id];
        if (a !== undefined) {
          const score = a.score;
          total += q.inverse ? 3 - score : score;
        }
      });
      const band = quiz.resultBands.find((b) => total >= b.min && total <= b.max);
      return { type: 'reflective-score', total, band };
    }

    return null;
  }, [showResult, answers, quiz]);

  const whatsappLink = useMemo(() => {
    if (!quiz) return profile.whatsapp;
    let msg = `Hola Karina! Hice el quiz "${quiz.title}" en tu sitio y me gustaría hablar.`;
    if (result) {
      if (result.type === 'validated') {
        msg += `\nResultado EPDS: ${result.total}/30 — ${result.band.title}.`;
      } else if (result.type === 'reflective-category') {
        msg += `\nResultado: ${result.data.title}.`;
      } else if (result.type === 'reflective-score') {
        msg += `\nResultado: ${result.band.title} (${result.total}/24).`;
      }
    }
    return `${profile.whatsapp}?text=${encodeURIComponent(msg)}`;
  }, [result, quiz]);

  if (!quiz) return <Navigate to="/quizzes" replace />;

  const totalSteps = quiz.questions.length + 1; // +1 for intro
  const onIntro = stepIdx === 0;
  const currentQuestion = onIntro ? null : quiz.questions[stepIdx - 1];
  const progress = showResult
    ? 100
    : Math.round(((stepIdx) / totalSteps) * 100);

  const select = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const goNext = () => {
    if (!onIntro && answers[currentQuestion.id] === undefined) return;
    if (stepIdx < totalSteps - 1) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setShowResult(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrev = () => {
    if (showResult) {
      setShowResult(false);
      return;
    }
    if (stepIdx > 0) {
      setStepIdx(stepIdx - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const restart = () => {
    setStepIdx(0);
    setAnswers({});
    setShowResult(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canAdvance = onIntro || answers[currentQuestion?.id] !== undefined;

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/90 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/quizzes" className="flex items-center gap-3 group">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Karina Negrín</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Quizzes & reflexiones
              </span>
            </span>
          </Link>
          <Link
            to="/quizzes"
            className="text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
          >
            ← Todos los quizzes
          </Link>
        </div>
        <div className="h-1 w-full bg-[#E8DDC8]">
          <div
            className="h-full bg-[#8FA08A] transition-[width] duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-5 lg:px-8 py-10 lg:py-14">
        {/* Intro */}
        {!showResult && onIntro && (
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-9 h-px bg-[#8FA08A]" />
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
                {quiz.type === 'validated' ? 'Test validado' : 'Reflexión · no diagnóstico'}
              </span>
            </div>
            <h1 className="font-serif text-[36px] sm:text-[48px] lg:text-[56px] leading-[1.04] tracking-[-0.02em]">
              {quiz.title}
            </h1>
            <p className="font-script text-[#B8806E] text-[26px] sm:text-[30px] mt-2">
              {quiz.subtitle}
            </p>

            <p className="mt-7 text-[16.5px] leading-[1.8] text-[#2E2A23]/85 font-light max-w-[640px]">
              {quiz.intro}
            </p>

            <div
              className={`mt-8 rounded-2xl p-5 border flex gap-4 items-start ${
                quiz.type === 'validated'
                  ? 'bg-[#E5EAE0] border-[#C5D2BB] text-[#3F4D3D]'
                  : 'bg-[#F8E8E0] border-[#E5C8BD] text-[#7A4F40]'
              }`}
            >
              {quiz.type === 'validated' ? (
                <ShieldCheck size={20} strokeWidth={1.6} className="shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle size={20} strokeWidth={1.6} className="shrink-0 mt-0.5" />
              )}
              <div className="text-[14px] leading-[1.65]">
                <p className="font-medium mb-1">
                  {quiz.type === 'validated'
                    ? 'Sobre esta herramienta'
                    : 'Importante antes de empezar'}
                </p>
                <p>{quiz.warning}</p>
                {quiz.citation && (
                  <p className="mt-2 text-[12.5px] italic opacity-80">{quiz.citation}</p>
                )}
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-[#DBCEB4] flex items-center justify-between flex-wrap gap-3">
              <Link to="/quizzes" className="btn-ghost">
                <ArrowLeft size={16} strokeWidth={1.6} />
                Volver
              </Link>
              <button onClick={goNext} className="btn-primary">
                Empezar
                <ArrowRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}

        {/* Question */}
        {!showResult && !onIntro && currentQuestion && (
          <div key={currentQuestion.id} className="fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-9 h-px bg-[#8FA08A]" />
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
                Pregunta {stepIdx} de {quiz.questions.length}
              </span>
            </div>
            <h2 className="font-serif text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.15] tracking-[-0.01em] max-w-[680px]">
              {currentQuestion.text}
            </h2>

            {/* Options */}
            <div className="mt-9 space-y-3">
              {(quiz.scale || currentQuestion.options).map((opt, i) => {
                const isScale = !!quiz.scale;
                const isSelected = isScale
                  ? answers[currentQuestion.id]?.score === opt.score
                  : answers[currentQuestion.id]?.label === opt.label;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => select(currentQuestion.id, opt)}
                    className={`w-full text-left px-5 py-4 rounded-2xl border text-[15.5px] leading-[1.5] transition-all duration-300 ${
                      isSelected
                        ? 'bg-[#B8806E] text-[#FAF4E8] border-[#B8806E] shadow-[0_10px_30px_-15px_rgba(155,80,56,0.55)]'
                        : 'bg-[#FAF4E8] text-[#2E2A23] border-[#DBCEB4] hover:border-[#B8806E]/60 hover:bg-[#FAF4E8]'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`w-3.5 h-3.5 rounded-full shrink-0 transition-colors ${
                          isSelected ? 'bg-[#FAF4E8]' : 'bg-transparent border border-[#C5B496]'
                        }`}
                      />
                      <span>{opt.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {currentQuestion.sensitive && (
              <p className="mt-5 text-[13px] text-[#8A7E6E] italic leading-[1.6]">
                Esta pregunta es delicada. Te pido que respondas con honestidad — esto es
                completamente confidencial y solo lo verás vos.
              </p>
            )}

            <div className="mt-12 pt-8 border-t border-[#DBCEB4] flex items-center justify-between gap-4 flex-wrap">
              <button onClick={goPrev} className="btn-ghost">
                <ArrowLeft size={16} strokeWidth={1.6} />
                Atrás
              </button>
              <button onClick={goNext} disabled={!canAdvance} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {stepIdx === quiz.questions.length ? 'Ver resultado' : 'Siguiente'}
                <ArrowRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {showResult && result && (
          <ResultView
            quiz={quiz}
            result={result}
            whatsappLink={whatsappLink}
            onRestart={restart}
          />
        )}
      </main>
    </div>
  );
};

// ============= Result View Component =============

const ResultView = ({ quiz, result, whatsappLink, onRestart }) => {
  const isValidated = result.type === 'validated';
  const isReflectiveCat = result.type === 'reflective-category';
  const isReflectiveScore = result.type === 'reflective-score';

  const data = isValidated
    ? result.band
    : isReflectiveScore
    ? result.band
    : result.data;

  const isUrgent = data.urgentTone || (isValidated && result.band.min >= 13);

  return (
    <div className="fade-up">
      {/* Self-harm alert (EPDS only) */}
      {isValidated && result.selfHarm && (
        <div className="mb-8 bg-[#F8E8E0] border-2 border-[#C9665A] rounded-2xl p-6">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={22} strokeWidth={1.7} className="text-[#C9665A] shrink-0 mt-0.5" />
            <div>
              <h3 className="font-serif text-[22px] text-[#7A2F22] leading-tight">
                {quiz.selfHarmAlert.title}
              </h3>
            </div>
          </div>
          <p className="text-[15px] leading-[1.75] text-[#5A2418]">
            {quiz.selfHarmAlert.body}
          </p>
          <div className="mt-5 space-y-2.5">
            {quiz.selfHarmAlert.resources.map((r, i) => (
              <a
                key={i}
                href={r.href}
                target="_blank"
                rel="noreferrer"
                className="block bg-[#FAF4E8] border border-[#C9665A]/40 rounded-xl px-4 py-3 hover:bg-[#F8E8E0] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Phone size={16} strokeWidth={1.7} className="text-[#C9665A]" />
                  <div>
                    <p className="font-medium text-[#7A2F22] text-[15px]">{r.label}</p>
                    <p className="text-[12.5px] text-[#7A4F40]">{r.meta}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Result header */}
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-block w-9 h-px bg-[#8FA08A]" />
        <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
          {isValidated ? 'Resultado del screening' : 'Tu resultado reflexivo'}
        </span>
      </div>

      <h1 className="font-serif text-[34px] sm:text-[48px] lg:text-[54px] leading-[1.05] tracking-[-0.02em]">
        {data.title}
      </h1>
      <p className="font-script text-[#B8806E] text-[24px] sm:text-[28px] mt-3 leading-tight">
        {data.headline}
      </p>

      {/* Score badge for validated/score quizzes */}
      {(isValidated || isReflectiveScore) && (
        <div className="mt-6 inline-flex items-baseline gap-2 bg-[#FAF4E8] border border-[#DBCEB4] rounded-full px-5 py-2.5">
          <span className="text-[12px] tracking-[0.18em] uppercase text-[#8A7E6E]">
            Puntaje
          </span>
          <span className="font-serif text-[24px] text-[#B8806E] leading-none">
            {result.total}
            <span className="text-[#8A7E6E]/60 text-[16px]"> / {isValidated ? 30 : 24}</span>
          </span>
        </div>
      )}

      {/* Body */}
      <p className="mt-7 text-[16.5px] leading-[1.85] text-[#2E2A23]/85 font-light max-w-[680px]">
        {data.body}
      </p>

      {/* Practices (reflective only) */}
      {data.practices && data.practices.length > 0 && (
        <div className="mt-9 bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} strokeWidth={1.7} className="text-[#B8806E]" />
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#8A7E6E]">
              Algunas invitaciones concretas
            </span>
          </div>
          <ul className="space-y-3">
            {data.practices.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] leading-[1.7] text-[#2E2A23]/85">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#B8806E] shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-9 bg-[#E5EAE0] border border-[#C5D2BB] rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} strokeWidth={1.7} className="text-[#5F7259] shrink-0 mt-0.5" />
          <div className="text-[14px] leading-[1.7] text-[#3F4D3D]">
            {isValidated ? (
              <p>
                <strong>Este es un screening, no un diagnóstico.</strong> El resultado es
                orientativo y refleja únicamente lo que respondiste hoy. Solo un/a profesional
                puede establecer un diagnóstico clínico en una consulta. Si algo te resonó —
                aunque el puntaje sea bajo — vale la pena hablarlo.
              </p>
            ) : (
              <p>
                <strong>Este resultado NO es un diagnóstico ni una etiqueta.</strong> Es una
                propuesta reflexiva basada en literatura especializada. Cada persona, cada
                vínculo, cada momento es único, y solo en una consulta clínica se puede mirar
                con la profundidad que merece.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* CTA WhatsApp */}
      <div
        className={`mt-9 rounded-2xl p-7 sm:p-8 ${
          isUrgent
            ? 'bg-[#3D2817] text-[#FAF4E8] border border-[#5C3D2A]'
            : 'bg-[#2D3A2D] text-[#FAF4E8]'
        }`}
      >
        <Quote size={28} strokeWidth={1} className="text-[#EBC8C0] mb-3" />
        <p className="font-serif text-[20px] sm:text-[24px] leading-[1.45] italic">
          {isUrgent
            ? 'No tenés que atravesar esto sola. Si querés, escribime y conversamos cuándo y cómo encontrarnos.'
            : 'Si lo que apareció en este quiz te dejó pensando, conversemos. Te respondo personalmente.'}
        </p>
        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full bg-[#B8806E] text-[#FAF4E8] font-medium tracking-[0.08em] uppercase text-[13px] hover:bg-[#9C6856] transition-colors"
        >
          <MessageCircle size={16} strokeWidth={1.7} />
          Conversar con Karina
        </a>
      </div>

      {/* Professional sign */}
      <div className="mt-10 pt-7 border-t border-[#DBCEB4] flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-[20px] leading-none shrink-0">
            k
          </div>
          <div>
            <p className="font-serif text-[18px] text-[#2E2A23] leading-tight">
              {PROFESSIONAL_SIGN.name}
            </p>
            <p className="text-[12.5px] tracking-[0.06em] text-[#8A7E6E] mt-1">
              {PROFESSIONAL_SIGN.role}
            </p>
            <p className="text-[12px] tracking-[0.18em] uppercase text-[#8FA08A] mt-1">
              {PROFESSIONAL_SIGN.matricula}
            </p>
          </div>
        </div>
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.7} />
          Hacer el quiz de nuevo
        </button>
      </div>
    </div>
  );
};

export default Quiz;
